import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { setGlobalTenant } from '@/utils/urlUtils';

interface TenantContextType {
  tenant: string | null;
  setTenant: (tenant: string | null) => void;
  isValidTenant: (tenantName: string) => boolean;
  clearTenant: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenantState] = useState<string | null>(null);

  const isValidTenant = useCallback((tenantName: string): boolean => {
    if (!tenantName || typeof tenantName !== 'string') return false;
    
    // Validar formato: apenas caracteres seguros para URL
    const isValidFormat = /^[a-zA-Z0-9_-]+$/.test(tenantName);
    const hasValidLength = tenantName.length > 0 && tenantName.length <= 100;
    const isNotUrl = !tenantName.startsWith('http') && 
                     !tenantName.includes('://') && 
                     !tenantName.includes('..') && 
                     !tenantName.includes('/');
    
    return isValidFormat && hasValidLength && isNotUrl;
  }, []);

  const setTenant = useCallback((newTenant: string | null) => {
    if (newTenant === null) {
      setTenantState(null);
      setGlobalTenant(null);
      return;
    }

    if (isValidTenant(newTenant)) {
      setTenantState(newTenant);
      setGlobalTenant(newTenant);
    } else {
      console.warn('Invalid tenant name:', newTenant);
      setTenantState(null);
      setGlobalTenant(null);
    }
  }, [isValidTenant]);

  const clearTenant = useCallback(() => {
    setTenantState(null);
    setGlobalTenant(null);
  }, []);

  const value: TenantContextType = {
    tenant,
    setTenant,
    isValidTenant,
    clearTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}